import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Calendar, Clock, ArrowRight, Search, ChevronLeft, ChevronRight, 
  Eye, Heart, MessageCircle, Filter, X, TrendingUp, Bookmark, BookmarkCheck,
  Mail, Phone, MapPin, Award, Shield, Zap, DollarSign, CheckCircle, 
  Share2, User, ThumbsUp, Loader, Sparkles, Newspaper, Tag, Facebook, Twitter, Linkedin
} from "lucide-react";
import { FaFacebook, FaTwitter, FaLinkedin, FaInstagram } from "react-icons/fa";

const Blog = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedTag, setSelectedTag] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState("latest");
  const [bookmarkedPosts, setBookmarkedPosts] = useState([]);
  const [likedPosts, setLikedPosts] = useState([]);
  const [showNewsletter, setShowNewsletter] = useState(false);
  const postsPerPage = 6;

  // Show newsletter popup after 5 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowNewsletter(true);
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  const blogPosts = [
    {
      id: 1,
      title: "How to Get a Personal Loan with Low CIBIL Score?",
      excerpt: "Getting a personal loan with a low CIBIL score might seem challenging, but it's not impossible. Learn expert tips and strategies to improve your chances of approval...",
      image: "https://placehold.co/600x400/22c55e/white?text=Personal+Loan",
      author: "Rahul Sharma",
      authorAvatar: "https://placehold.co/100x100/22c55e/white?text=RS",
      authorRole: "Senior Financial Advisor",
      date: "March 15, 2026",
      readTime: "5 min read",
      category: "Personal Loan",
      path: "/loan",
      tags: ["CIBIL Score", "Personal Loan", "Credit Score", "Loan Tips"],
      views: 1250,
      likes: 89,
      comments: 12,
      featured: true
    },
    {
      id: 2,
      title: "Home Loan Eligibility Criteria 2026: Complete Guide",
      excerpt: "Understanding home loan eligibility is crucial before applying. Check out the complete guide on income requirements, credit score needs, and documentation...",
      image: "https://placehold.co/600x400/3b82f6/white?text=Home+Loan",
      author: "Priya Mehta",
      authorAvatar: "https://placehold.co/100x100/3b82f6/white?text=PM",
      authorRole: "Home Loan Expert",
      date: "March 12, 2026",
      readTime: "7 min read",
      category: "Home Loan",
      path: "/blog/home",
      tags: ["Home Loan", "Eligibility", "Property", "Banking"],
      views: 890,
      likes: 67,
      comments: 8,
      featured: false
    },

  ];

  // All unique tags
  const allTags = [...new Set(blogPosts.flatMap(post => post.tags))];

  const toggleBookmark = (postId, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (bookmarkedPosts.includes(postId)) {
      setBookmarkedPosts(bookmarkedPosts.filter(id => id !== postId));
    } else {
      setBookmarkedPosts([...bookmarkedPosts, postId]);
    }
  };

  const toggleLike = (postId, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (likedPosts.includes(postId)) {
      setLikedPosts(likedPosts.filter(id => id !== postId));
    } else {
      setLikedPosts([...likedPosts, postId]);
    }
  };

  let filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === "All" || post.category === selectedCategory;
    const matchesTag = !selectedTag || post.tags.includes(selectedTag);
    return matchesSearch && matchesCategory && matchesTag;
  });

  if (sortBy === "latest") {
    filteredPosts.sort((a, b) => new Date(b.date) - new Date(a.date));
  } else if (sortBy === "popular") {
    filteredPosts.sort((a, b) => b.views - a.views);
  } else if (sortBy === "mostLiked") {
    filteredPosts.sort((a, b) => b.likes - a.likes);
  }

  // Get featured post
  const featuredPost = blogPosts.find(post => post.featured);

  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = filteredPosts.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);

  const popularPosts = [
    { id: 3, title: "Business Loan vs Personal Loan", views: 2100, path: "/blog/business" },
    { id: 1, title: "How to Get a Personal Loan with Low CIBIL Score?", views: 1250, path: "/loan" },
    { id: 2, title: "Home Loan Eligibility Criteria 2026", views: 890, path: "/blog/home" },
    { id: 4, title: "Car Refinance: Save Money", views: 670, path: "/blog/car" }
  ];

  const recentPosts = [...blogPosts].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 4);

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-green-700 via-green-600 to-emerald-700 text-white py-16 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-40 h-40 bg-yellow-400 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 text-center relative z-10">
          <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-1.5 mb-4 backdrop-blur-sm">
            <Newspaper size={16} />
            <span className="text-sm font-medium">Latest Financial Insights</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 bg-gradient-to-r from-white to-green-200 bg-clip-text text-transparent">
            Financial Insights Blog
          </h1>
          <p className="text-lg text-green-100 max-w-2xl mx-auto mb-8">
            Expert advice, tips, and guides to help you make smart financial decisions
          </p>
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search articles by title, topic, or tags..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                className="w-full pl-12 pr-4 py-3 rounded-xl text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-400 shadow-lg"
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm("")} className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-red-500">
                  <X size={18} />
                </button>
              )}
            </div>
          </div>
          {/* Stats */}
          <div className="flex justify-center gap-8 mt-8">
            <div className="text-center">
              <div className="text-2xl font-bold">{blogPosts.length}+</div>
              <div className="text-xs text-green-100">Articles</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">50K+</div>
              <div className="text-xs text-green-100">Monthly Readers</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">4.9⭐</div>
              <div className="text-xs text-green-100">Reader Rating</div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="flex flex-col lg:flex-row gap-10">
          {/* Left Side - Blog Cards */}
          <div className="lg:w-2/3">
            {/* Personal Loan Info Section */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden mb-8 border-l-4 border-green-600">
              <div className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <DollarSign className="text-green-600" size={24} />
                </div>
                
                <p className="text-gray-600 leading-relaxed mb-6">
                  A personal loan is an unsecured loan designed to meet your immediate financial needs without requiring any collateral. 
                  Whether it's funding a dream vacation, managing wedding expenses, renovating your home, covering medical emergencies, 
                  or consolidating debt, a personal loan offers the flexibility to use the funds as you see fit. With competitive interest 
                  rates and flexible repayment tenures, it's a smart choice for individuals seeking quick and hassle‑free financing. 
                  With low interest personal loans, flexible EMI options, and customizable repayment tenures, it becomes easier to manage 
                  your finances efficiently. Most lenders offer fast personal loan approval, minimal documentation, and quick disbursal, 
                  making it a hassle-free financing option. You can also apply for a personal loan online from the comfort of your home.
                </p>

                <div className="grid md:grid-cols-2 gap-4 mt-6 pt-4 border-t border-gray-100">
                  <div className="flex items-start gap-3">
                    <Shield size={18} className="text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-800 text-sm">Collateral‑free loan</h4>
                      <p className="text-xs text-gray-500">no need to pledge any assets</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Zap size={18} className="text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-800 text-sm">No end‑use restriction</h4>
                      <p className="text-xs text-gray-500">use the funds for any genuine personal need</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Calendar size={18} className="text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-800 text-sm">Repayment tenure up to 60 months</h4>
                      <p className="text-xs text-gray-500">choose a tenure that suits your budget</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle size={18} className="text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-800 text-sm">Minimal documentation</h4>
                      <p className="text-xs text-gray-500">digital uploads and simple paperwork</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Clock size={18} className="text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-800 text-sm">Quick disbursals</h4>
                      <p className="text-xs text-gray-500">funds credited within 24‑48 hours after approval</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <TrendingUp size={18} className="text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-800 text-sm">Interest Rate: Starting from 11.25%*</h4>
                      <p className="text-xs text-gray-500">we compare offers from multiple lenders to secure the best rate for you</p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 pt-3">
                  <Link to="/loan" className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-5 py-2.5 rounded-lg font-semibold text-sm transition-all duration-200 shadow-sm">
                    Apply for Personal Loan <ArrowRight size={16} />
                  </Link>
                </div>
              </div>
            </div>

            {/* Featured Post Section */}
            {featuredPost && (
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles size={20} className="text-yellow-500" />
                  <h2 className="text-lg font-bold text-gray-800">Featured Article</h2>
                </div>
                <Link to={featuredPost.path} className="block group">
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="relative h-64 md:h-full">
                        <img src={featuredPost.image} alt={featuredPost.title} className="w-full h-full object-cover" />
                        <div className="absolute top-3 left-3">
                          <span className="bg-yellow-500 text-white px-3 py-1 rounded-full text-xs font-bold">FEATURED</span>
                        </div>
                      </div>
                      <div className="p-6">
                        <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                          <Calendar size={12} /> {featuredPost.date}
                          <span className="mx-1">•</span>
                          <Clock size={12} /> {featuredPost.readTime}
                        </div>
                        <h3 className="font-bold text-gray-800 mb-3 group-hover:text-green-600 text-xl">{featuredPost.title}</h3>
                        <p className="text-gray-600 text-sm mb-4 line-clamp-3">{featuredPost.excerpt}</p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center text-white text-xs font-bold">
                              {featuredPost.authorAvatar ? featuredPost.authorAvatar.charAt(0) : "A"}
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-800">{featuredPost.author}</p>
                              <p className="text-xs text-gray-500">{featuredPost.authorRole}</p>
                            </div>
                          </div>
                          <span className="text-green-600 text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition">
                            Read More <ArrowRight size={14} />
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            )}

            {/* Filter Bar */}
            <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <button onClick={() => setShowFilters(!showFilters)} className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm">
                    <Filter size={16} /> Filters
                  </button>
                  <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                    <option value="latest">Latest</option>
                    <option value="popular">Most Popular</option>
                    <option value="mostLiked">Most Liked</option>
                  </select>
                </div>
                <div className="text-sm text-gray-500">
                  Showing {filteredPosts.length} of {blogPosts.length} articles
                </div>
              </div>
            </div>

            {/* Blog Cards */}
            {currentPosts.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-6">
                {currentPosts.map((post) => (
                  <Link key={post.id} to={post.path} className="block bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 group">
                    <div className="relative h-48 overflow-hidden">
                      <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
                      <div className="absolute top-3 right-3 flex gap-1">
                        <button onClick={(e) => toggleBookmark(post.id, e)} className="bg-white/90 p-1.5 rounded-full hover:bg-yellow-400 transition">
                          {bookmarkedPosts.includes(post.id) ? <BookmarkCheck size={14} className="text-yellow-500" /> : <Bookmark size={14} />}
                        </button>
                        <button onClick={(e) => toggleLike(post.id, e)} className="bg-white/90 p-1.5 rounded-full hover:bg-red-400 transition">
                          <Heart size={14} className={likedPosts.includes(post.id) ? "text-red-500 fill-red-500" : ""} />
                        </button>
                      </div>
                      <div className="absolute bottom-3 left-3">
                        <span className="bg-green-600 text-white px-2 py-1 rounded-lg text-xs font-semibold">{post.category}</span>
                      </div>
                      <div className="absolute bottom-3 right-3">
                        <span className="bg-black/60 text-white px-2 py-1 rounded-lg text-xs flex items-center gap-1">
                          <Clock size={10} /> {post.readTime}
                        </span>
                      </div>
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                        <Calendar size={12} /> {post.date}
                        <span className="mx-1">•</span>
                        <div className="flex items-center gap-1">
                          <User size={12} /> {post.author.split(" ")[0]}
                        </div>
                      </div>
                      <h3 className="font-bold text-gray-800 mb-2 group-hover:text-green-600 line-clamp-2 text-lg">{post.title}</h3>
                      <p className="text-gray-600 text-sm mb-3 line-clamp-2">{post.excerpt}</p>
                      <div className="flex flex-wrap gap-1 mb-3">
                        {post.tags.slice(0, 2).map(tag => (
                          <span key={tag} className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">#{tag}</span>
                        ))}
                        {post.tags.length > 2 && <span className="text-xs text-gray-400">+{post.tags.length - 2}</span>}
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          <span className="flex items-center gap-1"><Eye size={12} /> {post.views}</span>
                          <span className="flex items-center gap-1"><Heart size={12} className={likedPosts.includes(post.id) ? "text-red-500 fill-red-500" : ""} /> {post.likes + (likedPosts.includes(post.id) ? 1 : 0)}</span>
                          <span className="flex items-center gap-1"><MessageCircle size={12} /> {post.comments}</span>
                        </div>
                        <span className="text-green-600 text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition">
                          Read More <ArrowRight size={14} />
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-xl">
                <Search size={48} className="text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500">No articles found matching your criteria.</p>
                <button onClick={() => { setSearchTerm(""); setSelectedCategory("All"); setSelectedTag(""); }} className="mt-3 text-green-600 text-sm hover:underline">
                  Clear all filters
                </button>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-10">
                <button 
                  onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} 
                  disabled={currentPage === 1} 
                  className="p-2 rounded-lg border disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                >
                  <ChevronLeft size={18} />
                </button>
                {[...Array(totalPages)].map((_, i) => (
                  <button 
                    key={i} 
                    onClick={() => setCurrentPage(i + 1)} 
                    className={`w-9 h-9 rounded-lg transition ${currentPage === i + 1 ? "bg-green-600 text-white" : "border hover:bg-gray-100"}`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button 
                  onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} 
                  disabled={currentPage === totalPages} 
                  className="p-2 rounded-lg border disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            )}

            {/* Related Posts Section */}
            <div className="mt-12 pt-6 border-t border-gray-200">
              <div className="flex items-center gap-2 mb-6">
                <TrendingUp size={20} className="text-green-600" />
                <h3 className="font-bold text-gray-800 text-lg">You Might Also Like</h3>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                {blogPosts.slice(0, 3).map(post => (
                  <Link key={post.id} to={post.path} className="group">
                    <div className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition">
                      <div className="h-32 overflow-hidden">
                        <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-300" />
                      </div>
                      <div className="p-3">
                        <h4 className="font-medium text-gray-800 text-sm group-hover:text-green-600 line-clamp-2">{post.title}</h4>
                        <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
                          <Clock size={10} /> {post.readTime}
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="lg:w-1/3 space-y-6">
            {/* Author Spotlight */}
            <div className="bg-white rounded-xl shadow-md p-5">
              <h3 className="font-bold text-gray-800 text-lg mb-4 pb-3 border-b border-gray-200 flex items-center gap-2">
                <Award size={18} className="text-green-600" />
                Author Spotlight
              </h3>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-r from-green-600 to-emerald-600 flex items-center justify-center text-white font-bold">
                  RS
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800">Rahul Sharma</h4>
                  <p className="text-xs text-gray-500">Senior Financial Advisor</p>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-3">
                With over 10 years of experience in the banking and finance sector, Rahul helps readers make informed financial decisions.
              </p>
              <div className="flex gap-2">
                <span className="text-xs bg-green-100 text-green-600 px-2 py-1 rounded-full">Personal Loan Expert</span>
                <span className="text-xs bg-green-100 text-green-600 px-2 py-1 rounded-full">Credit Score Specialist</span>
              </div>
            </div>

            {/* Trending Tags Widget */}
            <div className="bg-white rounded-xl shadow-md p-5">
              <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-200">
                <Tag size={18} className="text-green-600" />
                <h3 className="font-bold text-gray-800 text-lg">Trending Tags</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {allTags.map(tag => (
                  <button
                    key={tag}
                    onClick={() => { setSelectedTag(selectedTag === tag ? "" : tag); setCurrentPage(1); }}
                    className={`px-3 py-1 rounded-full text-xs transition ${
                      selectedTag === tag 
                        ? "bg-green-600 text-white" 
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    #{tag}
                  </button>
                ))}
              </div>
            </div>

            {/* Recent Posts */}
            <div className="bg-white rounded-xl shadow-md p-5">
              <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-200">
                <Clock size={18} className="text-green-600" />
                <h3 className="font-bold text-gray-800 text-lg">Recent Posts</h3>
              </div>
              <div className="space-y-3">
                {recentPosts.map(post => (
                  <Link key={post.id} to={post.path} className="flex gap-3 group">
                    <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                      <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition" />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-800 text-sm group-hover:text-green-600 line-clamp-2">{post.title}</h4>
                      <p className="text-xs text-gray-400 mt-1">{post.date}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Most Popular */}
            <div className="bg-white rounded-xl shadow-md p-5">
              <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-200">
                <TrendingUp size={20} className="text-green-600" />
                <h3 className="font-bold text-gray-800 text-lg">Most Popular</h3>
              </div>
              <div className="space-y-3">
                {popularPosts.map((post, index) => (
                  <Link key={post.id} to={post.path} className="flex items-center justify-between group p-2 hover:bg-gray-50 rounded-lg transition">
                    <div className="flex items-center gap-3">
                      <span className="text-lg font-bold text-green-600 w-6">#{index + 1}</span>
                      <div>
                        <h4 className="font-medium text-gray-800 group-hover:text-green-600 text-sm line-clamp-1">
                          {post.title}
                        </h4>
                        <div className="flex items-center gap-1 mt-1">
                          <Eye size={10} className="text-gray-400" />
                          <span className="text-xs text-gray-400">{post.views} views</span>
                        </div>
                      </div>
                    </div>
                    <ArrowRight size={14} className="text-gray-400 group-hover:text-green-600 transition" />
                  </Link>
                ))}
              </div>
            </div>

            {/* Subscribe */}
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl p-5 text-white">
              <div className="text-center mb-4">
                <Mail size={24} className="text-white mx-auto mb-2" />
                <h3 className="font-bold text-lg mb-1">Subscribe Now</h3>
                <p className="text-green-100 text-xs">Get latest financial tips delivered to your inbox</p>
              </div>
              <input
                type="email"
                placeholder="Your email address"
                className="w-full px-4 py-2 rounded-lg text-gray-800 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-yellow-400"
              />
              <button className="w-full bg-yellow-400 text-gray-800 py-2 rounded-lg font-semibold text-sm hover:bg-yellow-300 transition">
                Subscribe
              </button>
              <p className="text-green-100 text-xs text-center mt-3">No spam, unsubscribe anytime.</p>
            </div>

            {/* Follow Us */}
            <div className="bg-white rounded-xl shadow-md p-5">
              <h3 className="font-bold text-gray-800 text-lg mb-4 pb-3 border-b border-gray-200">Follow Us</h3>
              <div className="flex justify-around">
                <a href="#" className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center hover:scale-110 transition"><FaFacebook size={18} /></a>
                <a href="#" className="w-10 h-10 bg-black text-white rounded-full flex items-center justify-center hover:scale-110 transition"><FaTwitter size={18} /></a>
                <a href="#" className="w-10 h-10 bg-pink-600 text-white rounded-full flex items-center justify-center hover:scale-110 transition"><FaInstagram size={18} /></a>
                <a href="#" className="w-10 h-10 bg-blue-700 text-white rounded-full flex items-center justify-center hover:scale-110 transition"><FaLinkedin size={18} /></a>
              </div>
            </div>

            {/* Need Help */}
            <div className="bg-white rounded-xl shadow-md p-5">
              <h3 className="font-bold text-gray-800 text-lg mb-4 pb-3 border-b border-gray-200">Need Help?</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Phone size={16} className="text-green-600" />
                  <span className="text-sm text-gray-600">+91 88871 41052</span>
                </div>
                <div className="flex items-center gap-3">
                  <Mail size={16} className="text-green-600" />
                  <span className="text-sm text-gray-600">info@cashpro.co.in</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Newsletter Popup */}
      {showNewsletter && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 relative animate-in fade-in zoom-in duration-300">
            <button onClick={() => setShowNewsletter(false)} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600">
              <X size={20} />
            </button>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail size={28} className="text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Don't Miss Out!</h3>
              <p className="text-gray-600 text-sm mb-4">Subscribe to get the latest financial insights and expert tips directly in your inbox.</p>
              <div className="flex gap-2">
                <input type="email" placeholder="Your email address" className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                <button className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-green-700">Subscribe</button>
              </div>
              <p className="text-xs text-gray-400 mt-3">We respect your privacy. Unsubscribe anytime.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Blog;